import { FillApplications } from "@/components/Dialogs/ApplicationSubmit";
import { Login } from "@/components/Dialogs/Login";
import { Register } from "@/components/Dialogs/Register";
import Spinner from "@/components/Spinner";
import { Avatar, AvatarFallback, AvatarImage } from "@radix-ui/react-avatar";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

export default function InviteDisplay({ userTeams, query }: { userTeams: any, query: any }) {
    const { id } = useParams();
    const navigate = useNavigate();
    const [applicationEnabled, setApplicationEnabled] = useState(false);
    const [error, setError] = useState("");
    const inviteDetails = useQuery({
        queryKey: ["invite", id],
        retry: 1,
        retryDelay: 1000,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        queryFn: async () => {
            const response = await fetch(`__SITE_BASE__/api/v1/invites/${id}?user=true`);
            return response.json();
        }
    });

    useEffect(() => {
        if (inviteDetails.data) setApplicationEnabled(inviteDetails.data?.invite?.applicationQuestions?.enabled);
        if (userTeams.data) {
            const team = userTeams.data.teams.find((team: any) => team.id === inviteDetails.data?.invite?.team.id);
            if (team) navigate(`/teams/${team.id}`);
        }
    }, [inviteDetails.data]);

    const joinTeamDirectly = async () => {
        const response = await fetch(`__SITE_BASE__/api/v1/invites/${id}/join`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
        });
        const data = await response.json();
        if (data.message) return setError(data.message);
        if(inviteDetails.data.invite.applicationEnabled) return navigate(`/`);
        else return window.location.href = `/teams/${inviteDetails.data.invite.team.id}`;
    }

    useEffect(() => {
        if (inviteDetails.data?.message && inviteDetails.data.message.includes("already a member")) {
            navigate(`/teams/${inviteDetails.data.team.id}`);
        }
    }, [inviteDetails.data]);

    return (
        <>
            {inviteDetails.isLoading && <Spinner />}
            {inviteDetails?.data?.invite && <div className="-ml-4 -mr-4 w-screen h-screen px-2 sm:px-0 flex items-center justify-center bg-gradient-to-b from-brand-green/20 to-brand-darkest">
                <div className="flex items-center h-96">
                    <div className="rounded-r-lg sm:rounded-r-none sm:rounded-l-lg gap-2 flex-col bg-brand-dark text-white h-full px-2 sm:px-32 py-2 w-auto flex items-center justify-center">
                        <div className="flex flex-col gap-2 items-center">
                            <Avatar className=" text-2xl rounded-full p-5">
                                <AvatarImage src={inviteDetails.data.invite.team.profilePicture} alt="Team Icon" className="w-32 rounded-full" />
                                <AvatarFallback className="h-32 flex items-center justify-center bg-brand-darkest rounded-full w-32">{inviteDetails.data.invite.team.name[0].toUpperCase()}</AvatarFallback>
                            </Avatar>
                            <p className="text-2xl font-bold text-center">You have been invited to join {inviteDetails.data.invite.team.name}</p>
                            {error && <p className="text-red-500">{error}</p>}
                        </div>
                        {query?.data?.user && <div className="flex flex-col gap-2 items-center">
                            {inviteDetails?.data?.invite?.applicationEnabled && <div className="flex flex-col gap-6 items-center mt-4">
                                {applicationEnabled && (
                                    <div className="flex flex-col gap-6 items-center mt-4">
                                        <p>Fill out an application to join the team</p>
                                        <FillApplications code={String(id)} teamId={inviteDetails.data.invite.team.id} questions={inviteDetails.data.invite.applicationQuestions.questions}>
                                            <span className="bg-brand-green text-white px-4 py-2 rounded-lg cursor-pointer">Fill Out Application</span>
                                        </FillApplications>
                                    </div>
                                )}
                                
                                {!applicationEnabled && <><p className="text-red-500">
                                    This team has disabled applications
                                </p>
                                    <button className="bg-brand-green text-white px-4 py-2 rounded-lg" onClick={() => window.location.href = '/'}>Go Home</button></>
                                }
                            </div>}

                            {!inviteDetails?.data?.invite?.applicationEnabled && <div className="flex flex-col gap-6 items-center mt-4">
                                <button className="bg-brand-green text-white px-4 py-2 rounded-lg" onClick={() => {joinTeamDirectly()}}>Join Team</button>
                            </div>}
                        </div>}

                        {!query?.data?.user && <div className="flex gap-2 items-center mt-4 font-bold">
                            <Login>
                                <span className="bg-brand-green text-white px-4 py-2 rounded-lg cursor-pointer">Login</span>
                            </Login>
                            <hr className="w-16" />
                            <img src="/assets/images/logo.svg" alt="Logo" className="w-8" draggable="false" />
                            <hr className="w-16" />
                            <Register>
                                <span className="bg-brand-green text-white px-4 py-2 rounded-lg cursor-pointer">Register</span>
                            </Register>
                        </div>}

                    </div>
                    <div className="rounded-r-lg hidden sm:flex bg-gradient-to-br relative from-brand-green to-purple-600 text-white px-32 py-2 h-full w-128 items-center justify-center">
                        {inviteDetails.data.invite.team.banner && <img src={inviteDetails.data.invite.team.banner} alt="Team Banner" className="absolute inset-0 object-cover w-full h-full rounded-lg" />}
                        <div className="rounded-lg p-2 border-2 bg-gray-300/20 z-10 items-center justify-center flex">
                            <Avatar className=" text-2xl rounded-full p-5">
                                <AvatarImage src={inviteDetails.data.invite.team.profilePicture} alt="Team Icon" className="w-32 z-10 rounded-full" />
                                <AvatarFallback className="h-32 flex items-center justify-center bg-brand-darkest rounded-full w-32">{inviteDetails.data.invite.team.name[0].toUpperCase()}</AvatarFallback>
                            </Avatar>
                        </div>
                    </div>
                </div>
            </div>}
            {inviteDetails?.data?.message && <div className="h-screen flex-col w-full flex items-center justify-center">
                <div className="text-white text-center text-2xl font-bold">                    
                    {inviteDetails.data.message}
                </div>
                <button className="bg-brand-green text-white px-4 py-2 rounded-lg mt-4" onClick={() => window.location.href = '/'}>Go Home</button>
            </div>}
        </>
    );
}
